import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { PropertyType } from "@/lib/types";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type PropertyTypesContextType = {
  propertyTypes: PropertyType[];
  propertyTypeMap: Record<string, string>;
  isLoading: boolean;
  error: unknown;
};

// Create a default value with empty arrays to avoid undefined checks
const defaultContextValue: PropertyTypesContextType = {
  propertyTypes: [],
  propertyTypeMap: {},
  isLoading: true,
  error: null,
};

const PropertyTypesContext =
  createContext<PropertyTypesContextType>(defaultContextValue);

export function PropertyTypesProvider({ children }: { children: ReactNode }) {
  // Initial state from localStorage if available (to reduce loading flicker)
  const { data: propertyTypes = [], isLoading, error } = usePropertyTypes();

  // Build propertyTypeMap
  const propertyTypeMap = useMemo(() => {
    return propertyTypes.reduce((acc, type) => {
      acc[type.id.toString()] = type.display_name || type.name; // Fallback to name if display_name is missing
      return acc;
    }, {} as Record<string, string>);
  }, [propertyTypes]);

  const contextValue = useMemo(() => {
    return {
      propertyTypes,
      propertyTypeMap,
      isLoading,
      error,
    };
  }, [propertyTypes, propertyTypeMap, isLoading, error]);

  console.log("[PropertyTypesContext] Context Value:", {
    isLoading,
    propertyTypeMapSize: Object.keys(propertyTypeMap).length,
    propertyTypesCount: propertyTypes.length,
  });

  return (
    <PropertyTypesContext.Provider value={contextValue}>
      {children}
    </PropertyTypesContext.Provider>
  );
}

export function usePropertyTypesContext() {
  const context = useContext(PropertyTypesContext);
  if (!context) {
    throw new Error(
      "usePropertyTypesContext must be used within a PropertyTypesProvider"
    );
  }
  return context;
}
